---
id: utils-overview
title: Dev utils
owner: Platform Team
status: Active
lastReviewed: 2026-01-27
visibility: internal
tags:
  - utils
  - shared
  - internal
---

## Product Overview

This library provides shared utilities used across Rezi apps and services to
support common workflows like formatting, validation, and data helpers.

### Primary Workflow
Product teams import utilities from this package to handle common formatting,
parsing, and workflow helpers without duplicating logic across app layers.

### Business Logic
Utilities encode reusable rules for resume workflows, downloads, localization,
tracking, and session behavior so that apps enforce consistent behavior in
shared flows.

### Outcomes
The package reduces duplication, keeps cross-app behavior aligned, and provides
building blocks for product workflows such as resume download, tracking, and
localization.

### Out of Scope
Feature-level UI, app routing, and backend services are not implemented here.

## Technical Overview

Utilities are organized in subfolders that map to their usage areas and
imported by feature and app layers as building blocks. Functions are kept small,
well-tested, and focused on a single responsibility to encourage reuse.

### Entry Points
Most utilities are imported directly from `@rezi/utils/*` paths, with grouped
exports for folders like `libs/utils/src/constants`, `libs/utils/src/url`,
`libs/utils/src/parser`, and `libs/utils/src/toast`.

### Key Modules
Resume and review helpers live under `libs/utils/src/resume` and
`libs/utils/src/resume-review`, download helpers in `libs/utils/src/download`,
localization and i18n in `libs/utils/src/locales` and `libs/utils/src/i18n.ts`,
and tracking utilities in `libs/utils/src/tracking.ts` and
`libs/utils/src/send-event-tracking.ts`.

### Data and Services
Some helpers integrate with external services, such as Firebase Storage in
`libs/utils/src/download/picture.ts` and API-backed download endpoints in
`libs/utils/src/download/pdf.ts` and `libs/utils/src/download/docx.ts`.

### Integrations and Side Effects
Tracking helpers emit analytics events, download helpers trigger file downloads
and notifications, and session utilities under
`libs/utils/src/session-timeout` manage client-side activity tracking.

### Constraints and Risks
Because this package is a shared dependency, behavior changes can have
cross-app impact and require coordinated updates in consuming apps.

## Changelog

- 2026-01-27: Expanded product and technical overviews to cover business logic and key code paths.
- 2026-01-26: Updated frontmatter id to match kebabcase path.
- 2026-01-22: Updated overview content to reflect usage and folder context.
- 2026-01-21: Replaced `audience` with `visibility` in frontmatter.
